import { useI18n } from '../contexts/I18nContext';

export default function JourneyPreviewSection() {
  const { t } = useI18n();

  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-gradient-to-br from-white via-[#F8F5FF] to-white overflow-hidden">
      {/* Background Hero Image */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1920&q=80" 
          alt="" 
          className="w-full h-full object-cover"
        />
      </div>

      {/* Background Decorative Elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-[#9FFFCB]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#B18AE0]/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center">
          
          {/* Left Side - Content */}
          <div className="space-y-4 sm:space-y-6 md:space-y-8 order-2 lg:order-1">
            {/* Title */}
            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-[#2C2C2C]">
                {t.journeyPreview.title}
              </h2>
              <p className="text-base md:text-lg lg:text-xl text-[#707070] leading-relaxed">
                {t.journeyPreview.intro}
              </p>
            </div>

            {/* Content */}
            <p className="text-base md:text-lg text-[#707070] leading-relaxed">
              {t.journeyPreview.content}
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {t.journeyPreview.features.map((feature: string, index: number) => (
                <div 
                  key={index}
                  className="px-4 py-2 bg-white border-2 border-[#9FFFCB]/30 rounded-full flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-2 h-2 bg-[#5B3FD0] rounded-full"></div>
                  <span className="text-sm font-medium text-[#2C2C2C]">{feature}</span>
                </div>
              ))}
            </div>

            {/* Highlight Box */}
            <div className="relative mt-8">
              <div className="absolute inset-0 bg-gradient-to-r from-[#5B3FD0] to-[#B18AE0] rounded-2xl opacity-5"></div>
              <div className="relative px-6 md:px-8 py-6 border-l-4 border-[#5B3FD0] bg-white/50 backdrop-blur-sm rounded-2xl">
                <p className="text-lg md:text-xl font-semibold text-[#5B3FD0] italic">
                  {t.journeyPreview.highlight}
                </p>
              </div>
            </div>
          </div>

          {/* Right Side - Visual Content */}
          <div className="order-1 lg:order-2">
            <div className="relative">
              {/* Floating Decoration Shapes */}
              <div className="absolute -top-6 -left-6 w-16 h-16 bg-[#9FFFCB] opacity-20 rounded-2xl rotate-12 animate-float hidden lg:block" style={{ animationDuration: '5s' }}></div>
              <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-[#B18AE0] rounded-full opacity-20 animate-float hidden lg:block" style={{ animationDuration: '6s', animationDelay: '1s' }}></div>

              {/* Main Visual Container */}
              <div className="space-y-4 sm:space-y-6">
                {/* Hero Image with Dashboard Overlay */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&q=80" 
                    alt="Students collaborating" 
                    className="w-full h-64 sm:h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#5B3FD0]/90 via-[#5B3FD0]/50 to-transparent"></div>
                  
                  {/* Overlay Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6 md:p-8">
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20">
                      <h3 className="text-white font-bold text-lg sm:text-xl md:text-2xl mb-3">Your Journey Dashboard</h3>
                      <div className="space-y-3">
                        {/* Mini Progress Bar */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-white text-xs sm:text-sm">
                            <span>Overall Progress</span>
                            <span className="font-bold">68%</span>
                          </div>
                          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                            <div className="h-full bg-[#9FFFCB] rounded-full transition-all duration-1000" style={{ width: '68%' }}></div>
                          </div>
                        </div>
                        
                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-3 pt-2">
                          <div className="text-center">
                            <div className="text-[#9FFFCB] font-bold text-base sm:text-lg md:text-xl">12</div>
                            <div className="text-white/80 text-xs">Tasks Done</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[#9FFFCB] font-bold text-base sm:text-lg md:text-xl">3</div>
                            <div className="text-white/80 text-xs">In Progress</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[#9FFFCB] font-bold text-base sm:text-lg md:text-xl">5</div>
                            <div className="text-white/80 text-xs">Remaining</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Secondary Images Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative rounded-2xl overflow-hidden shadow-lg group">
                    <img 
                      src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&q=80" 
                      alt="Graduation success" 
                      className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#9FFFCB]/80 to-transparent"></div>
                    <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4">
                      <p className="text-[#2C2C2C] font-bold text-xs sm:text-sm">Achievement Unlocked</p>
                    </div>
                  </div>
                  
                  <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-[#9FFFCB] p-4 sm:p-6 flex flex-col justify-center items-center text-center">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#5B3FD0] rounded-full flex items-center justify-center mb-3">
                      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-[#5B3FD0] font-bold text-sm sm:text-base mb-1">Track Everything</p>
                    <p className="text-[#707070] text-xs">In One Place</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
